import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { cloneDeep } from 'lodash-es';
import { exhaustMap } from 'rxjs';
import { BaseState, RanneyWell, RanneyWellEquipment, RanneyWellEquipmentRequest } from '../../../model';
import { RanneyWellService } from '../../../services';

export function withEquipmentMethods() {
  return signalStoreFeature(
    { state: type<BaseState<RanneyWell>>() },
    withMethods(store => {
      const service = inject(RanneyWellService);
      const addEquipment = rxMethod<{ id: number; data: RanneyWellEquipmentRequest }>(
        exhaustMap(({ id, data }) =>
          service.addEquipment(id, data).pipe(
            tapResponse({
              next: rsp =>
                patchState(store, state => {
                  const active = cloneDeep(state.active);
                  if (active && active.id === id) {
                    if (active.ranneyWellEquipmentSet) {
                      active.ranneyWellEquipmentSet.push(rsp);
                    } else {
                      active.ranneyWellEquipmentSet = [rsp];
                    }
                  }
                  return { active };
                }),
              error: console.log,
            })
          )
        )
      );
      const deleteEquipment = rxMethod<{ id: number; equipmentId: number }>(
        exhaustMap(({ id, equipmentId }) =>
          service.deleteEquipment(equipmentId).pipe(
            tapResponse({
              next: () =>
                patchState(store, state => {
                  const active = cloneDeep(state.active);
                  if (active && active.id === id) {
                    active.ranneyWellEquipmentSet = active.ranneyWellEquipmentSet.filter(
                      equipment => equipment.id !== equipmentId
                    );
                  }
                  return { active };
                }),
              error: console.log,
            })
          )
        )
      );
      const editEquipment = rxMethod<{ equipmentId: number; data: RanneyWellEquipmentRequest }>(
        exhaustMap(({ equipmentId, data }) =>
          service.editEquipment(equipmentId, data).pipe(
            tapResponse({
              next: rsp => updateActiveEquipment(rsp),
              error: console.log,
            })
          )
        )
      );
      const updateActiveEquipment = (data: RanneyWellEquipment) =>
        patchState(store, state => {
          const active = cloneDeep(state.active);
          if (active && active.id === data.ranneyWellId) {
            const equipmentIdx = active.ranneyWellEquipmentSet.findIndex(equipment => equipment.id === data.id);
            if (equipmentIdx !== undefined && equipmentIdx !== -1) {
              active.ranneyWellEquipmentSet[equipmentIdx] = { ...data };
            }
          }
          return { active };
        });

      return { addEquipment, editEquipment, deleteEquipment };
    })
  );
}
